import { useEffect, useMemo, useRef } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import { useDashboard } from "../../context/dashboard_provider";
import { getBarTimeChartOptions } from "./chart_options";
import { useChartId } from "./chart_id_context";

ChartJS.register(
  TimeScale,
  LinearScale,
  CategoryScale,
  LineController,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

const STORAGE_KEY = "chart-legend-visibility";

function getHiddenLabels(chartId) {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return parsed[chartId] ?? [];
  } catch {
    return [];
  }
}

function setHiddenLabels(chartId, hiddenLabels) {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    if (hiddenLabels.length === 0) {
      delete parsed[chartId];
    } else {
      parsed[chartId] = hiddenLabels;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage errors
  }
}

function usePersistedLegend(data, options) {
  const chartId = useChartId();

  return useMemo(() => {
    const hiddenLabels = chartId ? getHiddenLabels(chartId) : [];

    // Apply stored hidden state to datasets
    const patchedData = {
      ...data,
      datasets: data.datasets.map((ds) => ({
        ...ds,
        hidden: ds.legendToggle === false ? false : hiddenLabels.includes(ds.label) ? true : ds.hidden,
      })),
    };

    // Add custom legend onClick that persists visibility
    const existingOnClick = options?.plugins?.legend?.onClick;
    const patchedOptions = {
      ...options,
      plugins: {
        ...options?.plugins,
        legend: {
          ...options?.plugins?.legend,
          onClick: (e, legendItem, legend) => {
            const dataset = legend.chart.data.datasets[legendItem.datasetIndex];
            if (dataset?.legendToggle === false) return;

            // Run default Chart.js toggle behaviour first
            if (existingOnClick) {
              existingOnClick(e, legendItem, legend);
            } else {
              ChartJS.defaults.plugins.legend.onClick.call(legend, e, legendItem, legend);
            }

            if (!chartId) return;

            // Persist the new visibility state
            const current = getHiddenLabels(chartId);
            const label = legendItem.text;
            const isNowHidden = !legend.chart.isDatasetVisible(legendItem.datasetIndex);
            const updated = isNowHidden ? [...new Set([...current, label])] : current.filter((l) => l !== label);
            setHiddenLabels(chartId, updated);
          },
        },
      },
    };

    return { data: patchedData, options: patchedOptions };
  }, [chartId, data, options]);
}

export function BarTimeChartSynced({ data, options }) {
  const { tooltipXPos } = useDashboard();
  const lastTooltip = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const handleHover = (e) => {
      const rect = chart.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const dataX = chart.scales.x.getValueForPixel(x);

      // Find index closest to cursor position on time axis
      const labels = chart.data.labels;
      let closestIndex = 0;
      let minDiff = Math.abs(labels[0] - dataX);

      for (let i = 1; i < labels.length; i++) {
        const diff = Math.abs(labels[i] - dataX);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = i;
        }
      }

      tooltipXPos.current = closestIndex;
    };

    const handleLeave = (e) => {
      tooltipXPos.current = null;
    };

    chart.canvas.onmousemove = handleHover;
    chart.canvas.onmouseleave = handleLeave;

    const interval = setInterval(() => {
      if (lastTooltip.current == tooltipXPos.current) {
        return;
      }

      const currentChart = chartRef.current;
      if (!currentChart || !currentChart.tooltip || currentChart.ctx === null) return;

      if (tooltipXPos.current && tooltipXPos.current !== null) {
        const index = tooltipXPos.current;
        if (index !== null && index >= 0) {
          currentChart.tooltip.setActiveElements(
            currentChart.config.data.datasets
              .map((dataset, dataSetIndex) => ({ datasetIndex: dataSetIndex, index: index }))
              .filter(({ datasetIndex, index: idx }) => {
                const ds = currentChart.data.datasets[datasetIndex];
                return ds && ds.data && idx < ds.data.length;
              }),
          );
        }
      } else {
        currentChart.tooltip.setActiveElements([]);
      }

      lastTooltip.current = tooltipXPos.current;
      currentChart.update();
    }, 100);

    return () => {
      clearInterval(interval);
      if (chart.canvas) {
        chart.canvas.onmousemove = null;
        chart.canvas.onmouseleave = null;
      }
    };
  }, []);

  const o = options ? options : getBarTimeChartOptions();
  o.events = ["click"];

  const persisted = usePersistedLegend(data, o);

  return <Bar ref={chartRef} data={persisted.data} width={"100%"} options={persisted.options} />;
}

export function BarTimeChart({ data, options }) {
  const o = options ? options : getBarTimeChartOptions();
  const persisted = usePersistedLegend(data, o);
  return <Bar data={persisted.data} width={"100%"} options={persisted.options} />;
}
